import {
  ADMIN_ACCOUNT,
  ADMIN_PASSWORD,
  createEmptyInspectionItems,
  createEmptyBuildingUnit,
  DEFAULT_GROUP_ID,
  DEFAULT_GROUP_NAME,
  EMPTY_BASIC_INFO,
  EMPTY_DECLARATION,
  EMPTY_GROUPS,
  EMPTY_OVERVIEW,
  EMPTY_PENDING_SYNC_CHANGES,
  EMPTY_RECEIVING,
  EMPTY_EXPORT_STATUS,
  EMPTY_USERS,
  MAX_GROUP_PROJECTS,
  STORAGE_KEY,
} from "./constants";
import { persistImageDataUrls } from "./photo-storage";
import { getReportTemplateDefaults } from "./report-defaults";
import type {
  AppState,
  DeclarationData,
  GroupSummary,
  InspectionDecision,
  InspectionItemData,
  InspectionSubitemData,
  ManagedUser,
  PendingSyncChange,
  PendingSyncScope,
  ProjectBasicInfo,
  ProjectOverview,
  ProjectSummary,
  ReceivingData,
  UserSession,
  UserRole,
  WorkflowStatus,
} from "./types";

const defaultState: AppState = {
  user: null,
  currentProjectId: null,
  groups: [],
  users: [],
  projects: [],
  pendingSyncChanges: [],
  deletedUserIds: [],
  deletedProjectIds: [],
};

let state: AppState = loadState();
let serverPersistTimer: number | null = null;
let serverPersistInFlight: Promise<void> = Promise.resolve();

function isInlineImageData(value: string): boolean {
  return value.startsWith("data:image/");
}

function inferSubitemWorkflowStatus(subitem: Partial<InspectionSubitemData>): WorkflowStatus {
  if (subitem.decision === "not_involved") {
    return "not_involved";
  }

  if (subitem.decision === "pass" || subitem.decision === "fail") {
    return "completed";
  }

  return "pending";
}

function inferItemWorkflowStatus(
  item: Partial<InspectionItemData>,
  subitems: InspectionSubitemData[],
): WorkflowStatus {
  if (item.decision === "not_involved" || subitems.every((subitem) => subitem.workflowStatus === "not_involved")) {
    return "not_involved";
  }

  if (subitems.every((subitem) => subitem.workflowStatus === "completed" || subitem.workflowStatus === "not_involved")) {
    return "completed";
  }

  return subitems.some((subitem) => subitem.workflowStatus !== "pending") ? "in_progress" : "pending";
}

function normalizeText(value: unknown): string {
  return String(value || "").trim();
}

function createGroupId(groupName: string): string {
  const normalized = normalizeText(groupName);
  if (!normalized) {
    return DEFAULT_GROUP_ID;
  }
  return `group_${normalized
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^\w\u4e00-\u9fa5-]/g, "") || "default"}`;
}

function normalizeGroupName(groupName: unknown): string {
  return normalizeText(groupName) || DEFAULT_GROUP_NAME;
}

function normalizeGroups(groups: GroupSummary[] | unknown[]): GroupSummary[] {
  const seen = new Set<string>();
  const normalizedGroups = [
    { id: DEFAULT_GROUP_ID, name: DEFAULT_GROUP_NAME },
    ...(groups || []).map((group) => {
      const candidate = (group || {}) as Partial<GroupSummary>;
      const name = normalizeGroupName(candidate.name || group);
      return {
        id: normalizeText(candidate.id) || createGroupId(name),
        name,
      };
    }),
  ];

  return normalizedGroups.filter((group) => {
    if (seen.has(group.id)) {
      return false;
    }
    seen.add(group.id);
    return true;
  });
}

function sameGroup(a: { groupId?: string; groupName?: string }, b: { groupId?: string; groupName?: string }): boolean {
  const aGroupId = normalizeText(a.groupId);
  const bGroupId = normalizeText(b.groupId);
  if (aGroupId && bGroupId && aGroupId === bGroupId) {
    return true;
  }

  return normalizeGroupName(a.groupName) === normalizeGroupName(b.groupName);
}

function normalizeUsers(users: ManagedUser[] | unknown[]): ManagedUser[] {
  const seen = new Set<string>();
  return (users || [])
    .map((user) => {
      const candidate = (user || {}) as Partial<ManagedUser>;
      const name = normalizeText(candidate.name || user);
      if (!name || seen.has(name)) {
        return null;
      }
      seen.add(name);
      const groupName = normalizeGroupName(candidate.groupName);
      return {
        id: candidate.id || makeId("managed_user"),
        name,
        password: normalizeText(candidate.password) || "123456",
        groupId: normalizeText(candidate.groupId) || createGroupId(groupName),
        groupName,
        createdAt: candidate.createdAt || new Date().toISOString(),
      };
    })
    .filter((user): user is ManagedUser => Boolean(user));
}

function normalizeIdList(ids: unknown): string[] {
  return Array.from(
    new Set(
      (Array.isArray(ids) ? ids : [])
        .map((id) => normalizeText(id))
        .filter(Boolean),
    ),
  );
}

function normalizeUserSession(user: Partial<UserSession> | null | undefined): UserSession | null {
  if (!user || !normalizeText(user.name)) {
    return null;
  }
  const role: UserRole = user.role === "admin" ? "admin" : "user";
  const groupName = normalizeGroupName(user.groupName);
  return {
    id: user.id || makeId(role === "admin" ? "admin" : "user"),
    name: normalizeText(user.name),
    lastLoginAt: user.lastLoginAt || new Date().toISOString(),
    role,
    groupId: role === "admin" ? "admin" : normalizeText(user.groupId) || createGroupId(groupName),
    groupName: role === "admin" ? "管理员" : groupName,
  };
}

function canAccessProject(project: ProjectSummary | null, user: UserSession | null): boolean {
  if (!project || !user) {
    return false;
  }
  if (user.role === "admin") {
    return true;
  }
  return project.creatorUserId === user.id || sameGroup(project, user);
}

function getVisibleProjects(appState: AppState): ProjectSummary[] {
  if (!appState.user) {
    return [];
  }
  return appState.projects.filter((project) => canAccessProject(project, appState.user));
}

function loadState(): AppState {
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return defaultState;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<AppState>;
    return {
      user: normalizeUserSession(parsed.user),
      currentProjectId: parsed.currentProjectId || null,
      groups: normalizeGroups(parsed.groups || EMPTY_GROUPS),
      users: normalizeUsers(parsed.users || EMPTY_USERS),
      projects: (parsed.projects || []).map(normalizeProject),
      pendingSyncChanges: parsed.pendingSyncChanges || EMPTY_PENDING_SYNC_CHANGES,
      deletedUserIds: normalizeIdList(parsed.deletedUserIds),
      deletedProjectIds: normalizeIdList(parsed.deletedProjectIds),
    };
  } catch {
    return defaultState;
  }
}

function normalizeProject(project: Partial<ProjectSummary>): ProjectSummary {
  const legacyBasicInfo = (project.basicInfo || {}) as Record<string, unknown>;
  const normalizedOverview: ProjectOverview = {
    ...EMPTY_OVERVIEW,
    ...(project.overview || {}),
    buildingUnits:
      project.overview?.buildingUnits?.length
        ? project.overview.buildingUnits.map((unit) => ({
            ...createEmptyBuildingUnit(),
            ...unit,
          }))
        : [
            {
              ...createEmptyBuildingUnit(),
              buildingName: String(legacyBasicInfo.buildingName || ""),
              structureType: String(legacyBasicInfo.structureType || ""),
              usageType: String(legacyBasicInfo.usageType || ""),
              fireResistanceLevel: String(legacyBasicInfo.fireResistanceLevel || ""),
              aboveGroundFloorCount: String(legacyBasicInfo.aboveGroundFloorCount || legacyBasicInfo.floorCount || ""),
              undergroundFloorCount: String(legacyBasicInfo.undergroundFloorCount || ""),
              buildingHeight: String(legacyBasicInfo.buildingHeight || ""),
              aboveGroundBuildingArea: String(legacyBasicInfo.aboveGroundBuildingArea || legacyBasicInfo.buildingArea || ""),
              undergroundBuildingArea: String(legacyBasicInfo.undergroundBuildingArea || ""),
            },
          ],
    decorationPart:
      project.overview?.decorationPart || String(legacyBasicInfo.decorationPart || ""),
    decorationArea:
      project.overview?.decorationArea || String(legacyBasicInfo.decorationArea || ""),
    decorationFloors:
      project.overview?.decorationFloors || String(legacyBasicInfo.decorationFloors || ""),
    insulationMaterialCategory:
      project.overview?.insulationMaterialCategory ||
      String(legacyBasicInfo.insulationMaterialCategory || ""),
    insulationFloors:
      project.overview?.insulationFloors || String(legacyBasicInfo.insulationFloors || ""),
    insulationPart:
      project.overview?.insulationPart || String(legacyBasicInfo.insulationPart || ""),
    insulationMaterial:
      project.overview?.insulationMaterial || String(legacyBasicInfo.insulationMaterial || ""),
    fireFacilitySystems: Array.isArray(project.overview?.fireFacilitySystems)
      ? project.overview.fireFacilitySystems
      : [],
  };

  return {
    id: project.id || makeId("project"),
    name: project.name || "未命名项目",
    status: project.status || "draft",
    createdAt: project.createdAt || new Date().toISOString(),
    updatedAt: project.updatedAt || new Date().toISOString(),
    lastSyncedAt: project.lastSyncedAt || "",
    groupId: project.groupId || DEFAULT_GROUP_ID,
    groupName: project.groupName || DEFAULT_GROUP_NAME,
    creatorUserId: project.creatorUserId || "",
    creatorUserName: project.creatorUserName || "",
    basicInfo: {
      ...EMPTY_BASIC_INFO,
      ...(project.basicInfo || {}),
      constructionNature: Array.isArray(project.basicInfo?.constructionNature)
        ? project.basicInfo?.constructionNature
        : String(legacyBasicInfo.constructionNature || "")
            .split(/[、,，/\s]+/)
            .filter(Boolean),
      constructionUnits: Array.isArray(project.basicInfo?.constructionUnits)
        ? project.basicInfo?.constructionUnits
        : [String(legacyBasicInfo.constructionUnit || "") || ""],
      designCompanies: Array.isArray(project.basicInfo?.designCompanies)
        ? project.basicInfo?.designCompanies
        : [String(legacyBasicInfo.designCompany || "") || ""],
      constructionCompanies: Array.isArray(project.basicInfo?.constructionCompanies)
        ? project.basicInfo?.constructionCompanies
        : [String(legacyBasicInfo.constructionCompany || "") || ""],
      supervisionCompanies: Array.isArray(project.basicInfo?.supervisionCompanies)
        ? project.basicInfo?.supervisionCompanies
        : [String(legacyBasicInfo.supervisionCompany || "") || ""],
    },
    overview: normalizedOverview,
    declaration: {
      ...EMPTY_DECLARATION,
      ...(project.declaration || {}),
      recognition: {
        ...EMPTY_DECLARATION.recognition,
        ...(project.declaration?.recognition || {}),
      },
    },
    receiving: {
      ...EMPTY_RECEIVING,
      ...(project.receiving || {}),
      documentBody:
        project.receiving?.documentBody || EMPTY_RECEIVING.documentBody,
    },
    inspectionItems:
      project.inspectionItems?.map((item) => {
        const normalizedSubitems = item.subitems.map((subitem) => {
          const defaults = getReportTemplateDefaults(subitem.subitemId);
          const mergedSubitem = {
            ...subitem,
            sampleCount: subitem.sampleCount || defaults?.sampleCount || "",
            sampledLocations: subitem.sampledLocations || defaults?.sampledLocations || "",
            findings: subitem.findings || defaults?.findings || "",
            photos: subitem.photos || [],
          };

          return {
            ...mergedSubitem,
            workflowStatus: inferSubitemWorkflowStatus(mergedSubitem),
          };
        });

        return {
          ...item,
          subitems: normalizedSubitems,
          workflowStatus: inferItemWorkflowStatus(item, normalizedSubitems),
        };
      }) || createEmptyInspectionItems(),
    exportStatus: {
      ...EMPTY_EXPORT_STATUS,
      ...(project.exportStatus || {}),
    },
  };
}

function persistLocal(): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    if (error instanceof DOMException && error.name === "QuotaExceededError") {
      throw new Error("本地图片容量已接近上限，当前保存已被阻止。请先删除部分图片后重试。");
    }
    throw error;
  }
}

function buildSharedStatePayload() {
  return {
    groups: state.groups,
    users: state.users,
    projects: state.projects,
    pendingSyncChanges: state.pendingSyncChanges,
    deletedUserIds: state.deletedUserIds,
    deletedProjectIds: state.deletedProjectIds,
  };
}

function mergeGroups(...groupLists: GroupSummary[][]): GroupSummary[] {
  const allGroups = groupLists.flat();
  return normalizeGroups(allGroups);
}

function extractGroupsFromGroupedRecords(records: Array<{ groupId?: string; groupName?: string }>): GroupSummary[] {
  return records.map((record) => {
    const name = normalizeGroupName(record.groupName);
    return {
      id: normalizeText(record.groupId) || createGroupId(name),
      name,
    };
  });
}

function mergeUsersByIdentity(serverUsers: ManagedUser[], localUsers: ManagedUser[]): ManagedUser[] {
  const merged = [...serverUsers];
  const seenIds = new Set(merged.map((user) => user.id));
  const seenNames = new Set(merged.map((user) => user.name));

  for (const user of localUsers) {
    if (seenIds.has(user.id) || seenNames.has(user.name)) {
      continue;
    }
    merged.push(user);
    seenIds.add(user.id);
    seenNames.add(user.name);
  }

  return normalizeUsers(merged);
}

function mergeProjectsByUpdatedAt(serverProjects: ProjectSummary[], localProjects: ProjectSummary[]): ProjectSummary[] {
  const merged = new Map<string, ProjectSummary>();
  for (const project of serverProjects) {
    merged.set(project.id, project);
  }

  for (const project of localProjects) {
    const existing = merged.get(project.id);
    if (!existing || project.updatedAt.localeCompare(existing.updatedAt) > 0) {
      merged.set(project.id, project);
    }
  }

  return [...merged.values()];
}

function mergePendingSyncChanges(
  serverChanges: PendingSyncChange[],
  localChanges: PendingSyncChange[],
): PendingSyncChange[] {
  const merged = new Map<string, PendingSyncChange>();
  for (const change of serverChanges) {
    merged.set(change.id, change);
  }
  for (const change of localChanges) {
    merged.set(change.id, change);
  }
  return [...merged.values()].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

async function persistServerStateNow(): Promise<void> {
  await fetch("/api/h5/state", {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(buildSharedStatePayload()),
  });
}

function scheduleServerPersist(): void {
  if (serverPersistTimer !== null) {
    window.clearTimeout(serverPersistTimer);
  }

  serverPersistTimer = window.setTimeout(() => {
    serverPersistTimer = null;
    serverPersistInFlight = persistServerStateNow().catch((error) => {
      console.warn("Failed to persist shared H5 state.", error);
    });
  }, 250);
}

function persist(): void {
  persistLocal();
  scheduleServerPersist();
}

function makeId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

export function getState(): AppState {
  return state;
}

export async function flushServerState(): Promise<void> {
  if (serverPersistTimer !== null) {
    window.clearTimeout(serverPersistTimer);
    serverPersistTimer = null;
  }
  await serverPersistInFlight;
  serverPersistInFlight = persistServerStateNow();
  await serverPersistInFlight;
}

export async function initializeServerState(): Promise<void> {
  try {
    const response = await fetch("/api/h5/state", {
      headers: {
        Accept: "application/json",
      },
      cache: "no-store",
    });
    if (!response.ok) {
      return;
    }

    const payload = await response.json();
    const localUsers = state.users;
    const localProjects = state.projects;
    const localGroups = state.groups;
    const localPendingSyncChanges = state.pendingSyncChanges;
    const deletedUserIds = normalizeIdList([
      ...state.deletedUserIds,
      ...(Array.isArray(payload.deletedUserIds) ? payload.deletedUserIds : []),
    ]);
    const deletedProjectIds = normalizeIdList([
      ...state.deletedProjectIds,
      ...(Array.isArray(payload.deletedProjectIds) ? payload.deletedProjectIds : []),
    ]);
    const serverGroups = normalizeGroups(payload.groups || []);
    const serverUsers = normalizeUsers(payload.users || []).filter((user) => !deletedUserIds.includes(user.id));
    const serverProjects: ProjectSummary[] = ((payload.projects || []) as Partial<ProjectSummary>[])
      .map((project) => normalizeProject(project))
      .filter((project) => !deletedProjectIds.includes(project.id));
    const serverPendingSyncChanges = Array.isArray(payload.pendingSyncChanges)
      ? payload.pendingSyncChanges
      : EMPTY_PENDING_SYNC_CHANGES;
    const mergedUsers = mergeUsersByIdentity(serverUsers, localUsers).filter((user) => !deletedUserIds.includes(user.id));
    const mergedProjects = mergeProjectsByUpdatedAt(serverProjects, localProjects).filter(
      (project) => !deletedProjectIds.includes(project.id),
    );
    const mergedGroups = mergeGroups(
      serverGroups,
      localGroups,
      extractGroupsFromGroupedRecords(mergedUsers),
      extractGroupsFromGroupedRecords(mergedProjects),
    );
    const mergedPendingSyncChanges = mergePendingSyncChanges(serverPendingSyncChanges, localPendingSyncChanges);

    const currentUser =
      state.user?.role === "admin"
        ? state.user
        : state.user
          ? normalizeUserSession(
              mergedUsers.find((user) => user.id === state.user?.id || user.name === state.user?.name) || state.user,
            )
          : null;
    const currentProjectId = mergedProjects.some((project) => project.id === state.currentProjectId)
      ? state.currentProjectId
      : null;

    state = {
      ...state,
      user: currentUser,
      currentProjectId,
      groups: mergedGroups,
      users: mergedUsers,
      projects: mergedProjects,
      pendingSyncChanges: mergedPendingSyncChanges,
      deletedUserIds,
      deletedProjectIds,
    };
    persistLocal();
    scheduleServerPersist();
  } catch (error) {
    console.warn("Failed to initialize shared H5 state.", error);
  }
}

function enqueuePendingChange(
  projectId: string,
  scope: PendingSyncScope,
  entityId: string,
  label: string,
): void {
  const next: PendingSyncChange = {
    id: makeId("sync"),
    projectId,
    scope,
    entityId,
    label,
    updatedAt: new Date().toISOString(),
  };

  state = {
    ...state,
    pendingSyncChanges: [
      next,
      ...state.pendingSyncChanges.filter(
        (item) =>
          !(
            item.projectId === projectId &&
            item.scope === scope &&
            item.entityId === entityId
          ),
      ),
    ],
  };
}

function markProjectTouched(projectId: string): void {
  state = {
    ...state,
    projects: state.projects.map((project) =>
      project.id === projectId
        ? {
            ...project,
            updatedAt: new Date().toISOString(),
            status: "in_progress",
          }
        : project,
    ),
  };
}

export function loginManagedUser(name: string, password: string): { ok: true; user: UserSession } | { ok: false; message: string } {
  const normalizedName = normalizeText(name);
  const normalizedPassword = normalizeText(password);
  if (!normalizedName) {
    return { ok: false, message: "先填写姓名" };
  }
  if (!normalizedPassword) {
    return { ok: false, message: "先填写密码" };
  }

  const matchedByName = state.users.find((user) => user.name === normalizedName);
  if (matchedByName && matchedByName.password !== normalizedPassword) {
    return { ok: false, message: "用户姓名或密码不正确" };
  }

  const matchedUser =
    matchedByName ||
    {
      id: makeId("managed_user"),
      name: normalizedName,
      password: normalizedPassword,
      groupId: DEFAULT_GROUP_ID,
      groupName: DEFAULT_GROUP_NAME,
      createdAt: new Date().toISOString(),
    };

  const user: UserSession = {
    id: matchedUser.id,
    name: matchedUser.name,
    lastLoginAt: new Date().toISOString(),
    role: "user",
    groupId: matchedUser.groupId,
    groupName: matchedUser.groupName,
  };
  state = {
    ...state,
    user,
    users: matchedByName ? state.users : normalizeUsers([...state.users, matchedUser]),
  };
  persist();
  return { ok: true, user };
}

export function loginAdmin(account: string, password: string): { ok: true; user: UserSession } | { ok: false; message: string } {
  if (normalizeText(account) !== ADMIN_ACCOUNT || normalizeText(password) !== ADMIN_PASSWORD) {
    return { ok: false, message: "管理员账号或密码不正确" };
  }

  const user: UserSession = {
    id: "admin",
    name: "管理员",
    lastLoginAt: new Date().toISOString(),
    role: "admin",
    groupId: "admin",
    groupName: "管理员",
  };
  state = { ...state, user };
  persist();
  return { ok: true, user };
}

export function logout(): void {
  state = { ...state, user: null, currentProjectId: null };
  persist();
}

export function listUsers(): ManagedUser[] {
  return normalizeUsers(state.users);
}

export function listGroups(): GroupSummary[] {
  const groups: GroupSummary[] = [];
  const seen = new Set<string>();
  for (const group of state.groups) {
    const groupName = normalizeGroupName(group.name);
    const groupId = normalizeText(group.id) || createGroupId(groupName);
    if (!seen.has(groupId)) {
      seen.add(groupId);
      groups.push({ id: groupId, name: groupName });
    }
  }
  for (const user of state.users) {
    const groupName = normalizeGroupName(user.groupName);
    const groupId = normalizeText(user.groupId) || createGroupId(groupName);
    if (!seen.has(groupId)) {
      seen.add(groupId);
      groups.push({ id: groupId, name: groupName });
    }
  }
  for (const project of state.projects) {
    const groupName = normalizeGroupName(project.groupName);
    const groupId = normalizeText(project.groupId) || createGroupId(groupName);
    if (!seen.has(groupId)) {
      seen.add(groupId);
      groups.push({ id: groupId, name: groupName });
    }
  }
  return groups;
}

export function addGroup(groupName: string): { ok: true; groups: GroupSummary[] } | { ok: false; message: string } {
  const finalGroupName = normalizeGroupName(groupName);
  const finalGroupId = createGroupId(finalGroupName);
  if (state.groups.some((group) => group.id === finalGroupId || group.name === finalGroupName)) {
    return { ok: false, message: "这个小组已经存在" };
  }

  state = {
    ...state,
    groups: normalizeGroups([
      ...state.groups,
      {
        id: finalGroupId,
        name: finalGroupName,
      },
    ]),
  };
  persist();
  return { ok: true, groups: listGroups() };
}

export function addManagedUser(input: { name: string; password: string; groupName: string }): { ok: true; users: ManagedUser[] } | { ok: false; message: string } {
  const normalizedName = normalizeText(input.name);
  const normalizedPassword = normalizeText(input.password);
  const finalGroupName = normalizeGroupName(input.groupName);

  if (!normalizedName) {
    return { ok: false, message: "先填写用户姓名" };
  }
  if (!normalizedPassword) {
    return { ok: false, message: "先填写用户密码" };
  }
  if (state.users.some((user) => user.name === normalizedName)) {
    return { ok: false, message: "该用户已存在" };
  }

  const newUser = {
    id: makeId("managed_user"),
    name: normalizedName,
    password: normalizedPassword,
    groupId: createGroupId(finalGroupName),
    groupName: finalGroupName,
    createdAt: new Date().toISOString(),
  };
  state = {
    ...state,
    users: normalizeUsers([
      ...state.users,
      newUser,
    ]),
    deletedUserIds: state.deletedUserIds.filter((id) => id !== newUser.id),
  };
  persist();
  return { ok: true, users: state.users };
}

export function deleteManagedUser(userId: string): { ok: true; users: ManagedUser[] } {
  const nextUsers = state.users.filter((user) => user.id !== userId);
  const nextUserSession =
    state.user && state.user.role === "user" && !nextUsers.some((user) => user.id === state.user?.id)
      ? null
      : state.user;
  state = {
    ...state,
    users: normalizeUsers(nextUsers),
    user: nextUserSession,
    deletedUserIds: normalizeIdList([...state.deletedUserIds, userId]),
  };
  persist();
  return { ok: true, users: state.users };
}

export function updateManagedUserGroup(
  userId: string,
  groupName: string,
): { ok: true; users: ManagedUser[] } | { ok: false; message: string } {
  const finalGroupName = normalizeGroupName(groupName);
  const targetUser = state.users.find((user) => user.id === userId);
  if (!targetUser) {
    return { ok: false, message: "没有找到这个用户" };
  }

  if (targetUser.groupName === finalGroupName) {
    return { ok: false, message: "用户已经在这个小组里了" };
  }

  const nextUsers = state.users.map((user) =>
    user.id === userId
      ? {
          ...user,
          groupId: createGroupId(finalGroupName),
          groupName: finalGroupName,
        }
      : user,
  );

  const nextCurrentUser =
    state.user && state.user.role === "user" && state.user.id === userId
      ? {
          ...state.user,
          groupId: createGroupId(finalGroupName),
          groupName: finalGroupName,
        }
      : state.user;

  state = {
    ...state,
    groups: normalizeGroups([
      ...state.groups,
      {
        id: createGroupId(finalGroupName),
        name: finalGroupName,
      },
    ]),
    users: normalizeUsers(nextUsers),
    user: nextCurrentUser,
    projects: state.projects.map((project) =>
      project.creatorUserId === userId
        ? {
            ...project,
            groupId: createGroupId(finalGroupName),
            groupName: finalGroupName,
            updatedAt: new Date().toISOString(),
          }
        : project,
    ),
  };
  persist();
  return { ok: true, users: state.users };
}

export function listProjects(): ProjectSummary[] {
  return [...getVisibleProjects(state)].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function deleteProject(projectId: string): void {
  const target = state.projects.find((project) => project.id === projectId) || null;
  if (!canAccessProject(target, state.user)) {
    throw new Error("当前账号无权删除这个项目");
  }
  state = {
    ...state,
    currentProjectId: state.currentProjectId === projectId ? null : state.currentProjectId,
    projects: state.projects.filter((project) => project.id !== projectId),
    pendingSyncChanges: state.pendingSyncChanges.filter((change) => change.projectId !== projectId),
    deletedProjectIds: normalizeIdList([...state.deletedProjectIds, projectId]),
  };
  persist();
}

export function updateProjectGroup(projectId: string, groupName: string): { ok: true; project: ProjectSummary } | { ok: false; message: string } {
  const target = state.projects.find((project) => project.id === projectId) || null;
  if (!canAccessProject(target, state.user)) {
    return { ok: false, message: "当前账号无权调整这个项目分组" };
  }
  if (!target) {
    return { ok: false, message: "没有找到这个项目" };
  }

  const finalGroupName = normalizeGroupName(groupName);
  const finalGroupId = createGroupId(finalGroupName);
  if (target.groupId === finalGroupId && target.groupName === finalGroupName) {
    return { ok: false, message: "项目已经在这个小组里了" };
  }

  const targetGroupProjectCount = state.projects.filter(
    (project) => project.id !== projectId && project.groupId === finalGroupId,
  ).length;
  if (targetGroupProjectCount >= MAX_GROUP_PROJECTS) {
    return { ok: false, message: `本小组项目数量已满${MAX_GROUP_PROJECTS}个，请删除旧项目` };
  }

  let updatedProject: ProjectSummary | null = null;
  state = {
    ...state,
    groups: normalizeGroups([
      ...state.groups,
      {
        id: finalGroupId,
        name: finalGroupName,
      },
    ]),
    projects: state.projects.map((project) => {
      if (project.id !== projectId) {
        return project;
      }
      updatedProject = {
        ...project,
        groupId: finalGroupId,
        groupName: finalGroupName,
        updatedAt: new Date().toISOString(),
      };
      return updatedProject;
    }),
  };
  persist();

  if (!updatedProject) {
    return { ok: false, message: "调整项目分组失败，请稍后重试。" };
  }
  return { ok: true, project: updatedProject };
}

export function createProject(name: string): ProjectSummary {
  if (!state.user) {
    throw new Error("当前账号不能创建项目");
  }
  if (state.user.role !== "admin") {
    const visibleProjects = state.projects.filter((project) => canAccessProject(project, state.user));
    if (visibleProjects.length >= MAX_GROUP_PROJECTS) {
      throw new Error(`本小组项目数量已满${MAX_GROUP_PROJECTS}个，请删除旧项目`);
    }
  }

  const now = new Date().toISOString();
  const project: ProjectSummary = {
    id: makeId("project"),
    name,
    status: "draft",
    createdAt: now,
    updatedAt: now,
    lastSyncedAt: "",
    groupId: state.user.groupId || DEFAULT_GROUP_ID,
    groupName: state.user.groupName || DEFAULT_GROUP_NAME,
    creatorUserId: state.user.id,
    creatorUserName: state.user.name,
    basicInfo: {
      ...EMPTY_BASIC_INFO,
    },
    overview: {
      ...EMPTY_OVERVIEW,
      buildingUnits: [createEmptyBuildingUnit()],
    },
    declaration: { ...EMPTY_DECLARATION },
    receiving: { ...EMPTY_RECEIVING },
    inspectionItems: createEmptyInspectionItems(),
    exportStatus: { ...EMPTY_EXPORT_STATUS },
  };

  state = {
    ...state,
    currentProjectId: project.id,
    projects: [project, ...state.projects],
  };
  persist();
  return project;
}

export function renameProject(projectId: string, name: string): ProjectSummary | null {
  const trimmedName = name.trim();
  if (!trimmedName) {
    return null;
  }

  let updatedProject: ProjectSummary | null = null;

  state = {
    ...state,
    projects: state.projects.map((project) => {
      if (project.id !== projectId) {
        return project;
      }

      updatedProject = {
        ...project,
        name: trimmedName,
        updatedAt: new Date().toISOString(),
      };
      return updatedProject;
    }),
  };

  persist();
  return updatedProject;
}

export function getProjectPendingSyncCount(projectId: string): number {
  return state.pendingSyncChanges.filter((item) => item.projectId === projectId).length;
}

export function getPendingSyncChanges(projectId: string): PendingSyncChange[] {
  return state.pendingSyncChanges.filter((item) => item.projectId === projectId);
}

export function setCurrentProject(projectId: string): void {
  if (state.currentProjectId === projectId) {
    return;
  }
  state = { ...state, currentProjectId: projectId };
  try {
    persist();
  } catch (error) {
    console.warn("Failed to persist current project selection.", error);
  }
}

export function getCurrentProject(): ProjectSummary | null {
  const projectId = state.currentProjectId;
  if (!projectId) {
    return null;
  }
  return getProjectById(projectId);
}

export function getProjectById(projectId: string): ProjectSummary | null {
  const project = state.projects.find((item) => item.id === projectId) || null;
  return canAccessProject(project, state.user) ? project : null;
}

export function saveProjectBasicInfo(projectId: string, basicInfo: ProjectBasicInfo): ProjectSummary | null {
  let updatedProject: ProjectSummary | null = null;

  state = {
    ...state,
    projects: state.projects.map((project) => {
      if (project.id !== projectId) {
        return project;
      }

      updatedProject = {
        ...project,
        // Keep the project list title stable. The recognized declaration name
        // belongs to basic info and must not overwrite the user-created project title.
        basicInfo,
        updatedAt: new Date().toISOString(),
        exportStatus: { ...EMPTY_EXPORT_STATUS },
      };
      return updatedProject;
    }),
  };

  enqueuePendingChange(projectId, "basic_info", projectId, "基础信息待同步");
  markProjectTouched(projectId);
  updatedProject = getProjectById(projectId);

  persist();
  return updatedProject;
}

export function saveProjectOverview(projectId: string, overview: ProjectOverview): ProjectSummary | null {
  let updatedProject: ProjectSummary | null = null;

  state = {
    ...state,
    projects: state.projects.map((project) => {
      if (project.id !== projectId) {
        return project;
      }

      updatedProject = {
        ...project,
        overview,
        updatedAt: new Date().toISOString(),
        exportStatus: { ...EMPTY_EXPORT_STATUS },
      };
      return updatedProject;
    }),
  };

  enqueuePendingChange(projectId, "basic_info", `${projectId}_overview`, "项目概况待同步");
  markProjectTouched(projectId);
  updatedProject = getProjectById(projectId);

  persist();
  return updatedProject;
}

export function saveDeclarationData(
  projectId: string,
  declaration: DeclarationData,
): ProjectSummary | null {
  let updatedProject: ProjectSummary | null = null;

  state = {
    ...state,
    projects: state.projects.map((project) => {
      if (project.id !== projectId) {
        return project;
      }

      updatedProject = {
        ...project,
        declaration,
        exportStatus: { ...EMPTY_EXPORT_STATUS },
      };
      return updatedProject;
    }),
  };

  enqueuePendingChange(projectId, "declaration", projectId, "申报表与识别结果待同步");
  markProjectTouched(projectId);
  updatedProject = getProjectById(projectId);

  persist();
  return updatedProject;
}

export function saveReceivingData(
  projectId: string,
  receiving: ReceivingData,
): ProjectSummary | null {
  let updatedProject: ProjectSummary | null = null;

  state = {
    ...state,
    projects: state.projects.map((project) => {
      if (project.id !== projectId) {
        return project;
      }

      updatedProject = {
        ...project,
        receiving,
        exportStatus: { ...EMPTY_EXPORT_STATUS },
      };
      return updatedProject;
    }),
  };

  enqueuePendingChange(projectId, "receiving", projectId, "接件信息待同步");
  markProjectTouched(projectId);
  updatedProject = getProjectById(projectId);

  persist();
  return updatedProject;
}

export function getInspectionItem(projectId: string, itemId: string): InspectionItemData | null {
  const project = getProjectById(projectId);
  if (!project) {
    return null;
  }
  return project.inspectionItems.find((item) => item.itemId === itemId) || null;
}

export function getInspectionSubitem(
  projectId: string,
  itemId: string,
  subitemId: string,
): InspectionSubitemData | null {
  const item = getInspectionItem(projectId, itemId);
  if (!item) {
    return null;
  }
  return item.subitems.find((subitem) => subitem.subitemId === subitemId) || null;
}

function deriveItemDecision(subitems: InspectionSubitemData[]): InspectionDecision {
  if (subitems.every((subitem) => subitem.decision === "not_involved")) {
    return "not_involved";
  }
  if (subitems.some((subitem) => subitem.decision === "fail")) {
    return "fail";
  }
  if (
    subitems.length > 0 &&
    subitems.every((subitem) => subitem.decision === "pass" || subitem.decision === "not_involved") &&
    subitems.some((subitem) => subitem.decision === "pass")
  ) {
    return "pass";
  }
  return "pending";
}

export function saveInspectionSubitem(
  projectId: string,
  itemId: string,
  subitemId: string,
  patch: Pick<
    InspectionSubitemData,
    "sampleCount" | "sampledLocations" | "findings" | "decision" | "photos"
  >,
): ProjectSummary | null {
  let updatedProject: ProjectSummary | null = null;

  state = {
    ...state,
    projects: state.projects.map((project) => {
      if (project.id !== projectId) {
        return project;
      }

      const inspectionItems = project.inspectionItems.map((item) => {
        if (item.itemId !== itemId) {
          return item;
        }

        const subitems = item.subitems.map((subitem) => {
          const nextSubitem =
            subitem.subitemId !== subitemId
              ? subitem
              : {
                  ...subitem,
                  ...patch,
                };

          return {
            ...nextSubitem,
            workflowStatus: inferSubitemWorkflowStatus(nextSubitem),
          };
        });
        const nextItemDecision = deriveItemDecision(subitems);

        return {
          ...item,
          subitems,
          decision: nextItemDecision,
          workflowStatus: inferItemWorkflowStatus(
            {
              ...item,
              decision: nextItemDecision,
            },
            subitems,
          ),
        };
      });

      const nextProject: ProjectSummary = {
        ...project,
        inspectionItems,
        exportStatus: { ...EMPTY_EXPORT_STATUS },
      };
      updatedProject = nextProject;

      return nextProject;
    }),
  };

  enqueuePendingChange(projectId, "inspection_subitem", subitemId, `子项「${subitemId}」待同步`);
  markProjectTouched(projectId);
  updatedProject = getProjectById(projectId);

  persist();
  return updatedProject;
}

export function saveInspectionItemDecision(
  projectId: string,
  itemId: string,
  decision: InspectionDecision,
): ProjectSummary | null {
  let updatedProject: ProjectSummary | null = null;

  state = {
    ...state,
    projects: state.projects.map((project) => {
      if (project.id !== projectId) {
        return project;
      }

      const inspectionItems = project.inspectionItems.map<InspectionItemData>((item) => {
        if (item.itemId !== itemId) {
          return item;
        }

        if (decision === "not_involved") {
          return {
            ...item,
            decision: "not_involved" as InspectionDecision,
            workflowStatus: "not_involved" as WorkflowStatus,
            subitems: item.subitems.map((subitem) => ({
              ...subitem,
              sampleCount: "",
              sampledLocations: "",
              findings: "",
              photos: [] as string[],
              decision: "not_involved" as InspectionDecision,
              workflowStatus: "not_involved" as WorkflowStatus,
            })),
          };
        }

        return {
          ...item,
          decision: "pending" as InspectionDecision,
          workflowStatus: "pending" as WorkflowStatus,
          subitems: item.subitems.map((subitem) => ({
            ...subitem,
            decision:
              subitem.decision === "not_involved"
                ? ("pending" as InspectionDecision)
                : subitem.decision,
            workflowStatus:
              subitem.workflowStatus === "not_involved"
                ? ("pending" as WorkflowStatus)
                : inferSubitemWorkflowStatus(subitem),
          })),
        };
      });

      const nextProject: ProjectSummary = {
        ...project,
        inspectionItems,
        exportStatus: { ...EMPTY_EXPORT_STATUS },
      };
      updatedProject = nextProject;
      return nextProject;
    }),
  };

  enqueuePendingChange(projectId, "inspection_item", itemId, `单项「${itemId}」待同步`);
  markProjectTouched(projectId);
  updatedProject = getProjectById(projectId);

  persist();
  return updatedProject;
}

export function markProjectSynced(projectId: string): void {
  state = {
    ...state,
    projects: state.projects.map((project) =>
      project.id === projectId
        ? {
            ...project,
            lastSyncedAt: new Date().toISOString(),
          }
        : project,
    ),
    pendingSyncChanges: state.pendingSyncChanges.filter((item) => item.projectId !== projectId),
  };
  persist();
}

export function markExportGenerated(
  projectId: string,
  kind: "report" | "record",
  meta?: { exportRef?: string; fileName?: string },
): ProjectSummary | null {
  let updatedProject: ProjectSummary | null = null;
  const generatedAt = new Date().toISOString();

  state = {
    ...state,
    projects: state.projects.map((project) => {
      if (project.id !== projectId) {
        return project;
      }

      updatedProject = {
        ...project,
        exportStatus: {
          ...project.exportStatus,
          reportGeneratedAt:
            kind === "report" ? generatedAt : project.exportStatus.reportGeneratedAt,
          recordGeneratedAt:
            kind === "record" ? generatedAt : project.exportStatus.recordGeneratedAt,
          reportExportRef:
            kind === "report" ? meta?.exportRef || "" : project.exportStatus.reportExportRef,
          recordExportRef:
            kind === "record" ? meta?.exportRef || "" : project.exportStatus.recordExportRef,
          reportFileName:
            kind === "report" ? meta?.fileName || "" : project.exportStatus.reportFileName,
          recordFileName:
            kind === "record" ? meta?.fileName || "" : project.exportStatus.recordFileName,
        },
      };
      return updatedProject;
    }),
  };

  persist();
  return updatedProject;
}

export function clearExportGenerated(
  projectId: string,
  kind: "report" | "record",
): ProjectSummary | null {
  let updatedProject: ProjectSummary | null = null;

  state = {
    ...state,
    projects: state.projects.map((project) => {
      if (project.id !== projectId) {
        return project;
      }

      updatedProject = {
        ...project,
        exportStatus: {
          ...project.exportStatus,
          reportGeneratedAt:
            kind === "report" ? "" : project.exportStatus.reportGeneratedAt,
          recordGeneratedAt:
            kind === "record" ? "" : project.exportStatus.recordGeneratedAt,
          reportExportRef:
            kind === "report" ? "" : project.exportStatus.reportExportRef,
          recordExportRef:
            kind === "record" ? "" : project.exportStatus.recordExportRef,
          reportFileName:
            kind === "report" ? "" : project.exportStatus.reportFileName,
          recordFileName:
            kind === "record" ? "" : project.exportStatus.recordFileName,
        },
      };
      return updatedProject;
    }),
  };

  persist();
  return updatedProject;
}

export function markPreviewGenerated(
  projectId: string,
  kind: "report" | "record",
  meta?: { exportRef?: string; fileName?: string },
): ProjectSummary | null {
  let updatedProject: ProjectSummary | null = null;
  const generatedAt = new Date().toISOString();

  state = {
    ...state,
    projects: state.projects.map((project) => {
      if (project.id !== projectId) {
        return project;
      }

      updatedProject = {
        ...project,
        exportStatus: {
          ...project.exportStatus,
          reportPreviewGeneratedAt:
            kind === "report" ? generatedAt : project.exportStatus.reportPreviewGeneratedAt,
          recordPreviewGeneratedAt:
            kind === "record" ? generatedAt : project.exportStatus.recordPreviewGeneratedAt,
          reportPreviewRef:
            kind === "report" ? meta?.exportRef || "" : project.exportStatus.reportPreviewRef,
          recordPreviewRef:
            kind === "record" ? meta?.exportRef || "" : project.exportStatus.recordPreviewRef,
          reportPreviewFileName:
            kind === "report" ? meta?.fileName || "" : project.exportStatus.reportPreviewFileName,
          recordPreviewFileName:
            kind === "record" ? meta?.fileName || "" : project.exportStatus.recordPreviewFileName,
        },
      };
      return updatedProject;
    }),
  };

  persist();
  return updatedProject;
}

export async function migrateLegacyPhotoStorage(): Promise<void> {
  let touched = false;
  const nextProjects: ProjectSummary[] = [];

  for (const project of state.projects) {
    let changed = false;

    let declarationPdfRef = project.declaration.pdfFileDataUrl;
    if (isInlineImageData(project.declaration.pdfFileDataUrl) || project.declaration.pdfFileDataUrl.startsWith("data:application/pdf")) {
      const [ref] = await persistImageDataUrls([project.declaration.pdfFileDataUrl]);
      declarationPdfRef = ref || project.declaration.pdfFileDataUrl;
      changed = true;
    }

    const receivingInlinePhotos = project.receiving.photos.filter(isInlineImageData);
    const receivingPhotoMap = new Map<string, string>();
    if (receivingInlinePhotos.length) {
      const refs = await persistImageDataUrls(receivingInlinePhotos);
      receivingInlinePhotos.forEach((photo, index) => {
        receivingPhotoMap.set(photo, refs[index]);
      });
      changed = true;
    }

    const declarationInlinePhotos = project.declaration.photoDataUrls.filter(isInlineImageData);
    const declarationPhotoMap = new Map<string, string>();
    if (declarationInlinePhotos.length) {
      const refs = await persistImageDataUrls(declarationInlinePhotos);
      declarationInlinePhotos.forEach((photo, index) => {
        declarationPhotoMap.set(photo, refs[index]);
      });
      changed = true;
    }

    const subitemInlinePhotos = project.inspectionItems.flatMap((item) =>
      item.subitems.flatMap((subitem) => subitem.photos.filter(isInlineImageData)),
    );
    const subitemPhotoMap = new Map<string, string>();
    if (subitemInlinePhotos.length) {
      const refs = await persistImageDataUrls(subitemInlinePhotos);
      subitemInlinePhotos.forEach((photo, index) => {
        subitemPhotoMap.set(photo, refs[index]);
      });
      changed = true;
    }

    if (!changed) {
      nextProjects.push(project);
      continue;
    }

    touched = true;
    nextProjects.push({
      ...project,
      receiving: {
        ...project.receiving,
        photos: project.receiving.photos.map((photo) => receivingPhotoMap.get(photo) || photo),
      },
      declaration: {
        ...project.declaration,
        pdfFileDataUrl: declarationPdfRef,
        photoDataUrls: project.declaration.photoDataUrls.map((photo) => declarationPhotoMap.get(photo) || photo),
      },
      inspectionItems: project.inspectionItems.map((item) => ({
        ...item,
        subitems: item.subitems.map((subitem) => ({
          ...subitem,
          photos: subitem.photos.map((photo) => subitemPhotoMap.get(photo) || photo),
        })),
      })),
    });
  }

  if (!touched) {
    return;
  }

  state = {
    ...state,
    projects: nextProjects,
  };
  persist();
}
